/**
 * The partner's lifecycle strip at the top of the profile: status badge,
 * the moves available from that status (activate / suspend / archive /
 * restore / ban) and, once banned, the frozen-profile notice with the
 * recorded reason.
 */

import { Alert, Badge, Button, Group, Stack, Text } from '@mantine/core';

import { NP_STATUS_META, type NetworkPartnerDetail } from '@/types/network-partner';

export type PartnerStatusMove = 'active' | 'suspended';

interface PartnerLifecyclePanelProps {
  partner: NetworkPartnerDetail;
  onSetStatus: (status: PartnerStatusMove) => void;
  onArchive: () => void;
  onRestore: () => void;
  onBan: () => void;
}

export function PartnerLifecyclePanel({
  partner,
  onSetStatus,
  onArchive,
  onRestore,
  onBan,
}: Readonly<PartnerLifecyclePanelProps>) {
  const status = partner.status;
  const meta = NP_STATUS_META[status] ?? { label: status, color: 'gray' };
  const banned = status === 'banned';

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <Badge
            size="lg"
            variant={banned ? 'filled' : 'light'}
            color={meta.color}
            data-testid="np-status-badge"
          >
            {meta.label}
          </Badge>
          <Text size="sm" c="dimmed">
            {partner.email}
          </Text>
          {banned && partner.banned_at && (
            <Text size="sm" c="dimmed">
              Banned on {new Date(partner.banned_at).toLocaleDateString()}
            </Text>
          )}
        </Group>
        {!banned && (
          <LifecycleActions
            status={status}
            onSetStatus={onSetStatus}
            onArchive={onArchive}
            onRestore={onRestore}
            onBan={onBan}
          />
        )}
      </Group>
      {banned && (
        <Alert color="red" title="This partner is banned" data-testid="np-banned-alert">
          <Stack gap={4}>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {partner.ban_reason ?? 'No reason was recorded.'}
            </Text>
            <Text size="xs" c="dimmed">
              Banning is permanent — the profile is frozen and the partner can no longer be
              shortlisted or locked. Notes can still be added below.
            </Text>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

type LifecycleActionsProps = Omit<PartnerLifecyclePanelProps, 'partner'> & { status: string };

function LifecycleActions({
  status,
  onSetStatus,
  onArchive,
  onRestore,
  onBan,
}: Readonly<LifecycleActionsProps>) {
  return (
    <Group gap="xs">
      {status === 'prospect' && (
        <Button size="xs" color="green" variant="light" onClick={() => onSetStatus('active')}>
          Activate
        </Button>
      )}
      {status === 'suspended' && (
        <Button size="xs" color="green" variant="light" onClick={() => onSetStatus('active')}>
          Reactivate
        </Button>
      )}
      {status === 'active' && (
        <Button size="xs" color="orange" variant="light" onClick={() => onSetStatus('suspended')}>
          Suspend
        </Button>
      )}
      {status === 'archived' ? (
        <Button size="xs" variant="light" onClick={onRestore}>
          Restore as prospect
        </Button>
      ) : (
        <Button size="xs" color="red" variant="light" onClick={onArchive}>
          Archive
        </Button>
      )}
      <Button size="xs" color="red" variant="filled" onClick={onBan} data-testid="np-ban">
        Ban
      </Button>
    </Group>
  );
}
